<template>
  <div class="all-courses-page">
    <Navbar />
    <div class="activities-area">
      <div class="activities-container">
        <div class="activities-content-header">
          <p class="activities-date">
            {{ formatDate(activity.publish_at || activity.created_at) }}
          </p>
          <h2 class="activities-title">{{ getTitle(activity) }}</h2>
        </div>
      </div>
    </div>

    <!-- 主要內容區塊錨點 (位於 Banner 之下) -->
    <div id="main-content" tabindex="-1" class="main-content-anchor">
      <a
        id="accesskey-c"
        href="#accesskey-c"
        accesskey="C"
        class="accesskey-brick sr-only-focusable"
        title="主要內容區塊，快捷鍵：Alt+C"
        aria-label="主要內容區塊"
      >:::</a>
    </div>

    <div class="content">
      <div v-if="loading" class="loading-container">
        {{ t("common.loading") }}
      </div>
      <div v-else-if="activity.id">
        <!-- Featured Image -->
        <div v-if="getImage(activity)" class="feature-image-container" @click="openLightbox(getImage(activity))">
          <img :src="getImage(activity)" :alt="getTitle(activity)" class="feature-image" />
        </div>

        <!-- Lightbox -->
        <ImageLightbox :visible="showLightbox" :src="currentLightboxImage" :alt="getTitle(activity)"
          @close="closeLightbox" />

        <!-- Content -->
        <div class="activities-body activities-content" v-html="getContent(activity)" @click="handleBodyClick"></div>

        <!-- Info Section (Time/Location) -->
        <div class="info-section" v-if="activity.start_date || activity.locate">
          <div class="info-item" v-if="activity.start_date">
            <strong>{{ t("activities.activity_time") }}:</strong>
            {{ activity.start_date }} - {{ activity.end_date }} /
            {{ formatTime(activity.start_time) }} -
            {{ formatTime(activity.end_time) }}
          </div>
          <div class="info-item" v-if="activity.locate">
            <strong>{{ t("activities.location") }}:</strong>
            {{ getLocation(activity) }}
          </div>
        </div>

        <!-- Links -->
        <div class="links-section">
          <div class="reg-link" v-if="activity.reg_link">
            {{ t("activities.register") }}:
            <a v-if="getButtonStatus(activity) === 'not_open'" class="disabled-link">{{ t("activities.not_open_yet")
            }}</a>
            <a v-else-if="getButtonStatus(activity) === 'ended'" class="disabled-link">{{ t("activities.ended") }}</a>
            <a v-else-if="getButtonStatus(activity) === 'registration_closed'" class="disabled-link">{{
              t("activities.registration_closed") }}</a>
            <a v-else :href="activity.reg_link" target="_blank">{{
              t("activities.register_link")
            }}</a>
          </div>
          <div class="course-link" v-if="activity.course_link">
            {{ t("activities.more_info") }}:
            <a v-if="getButtonStatus(activity) === 'not_open'" class="disabled-link">{{ t("activities.not_open_yet")
            }}</a>
            <a v-else-if="getButtonStatus(activity) === 'ended'" class="disabled-link">{{ t("activities.ended") }}</a>
            <a v-else-if="getButtonStatus(activity) === 'registration_closed'" class="disabled-link">{{
              t("activities.registration_closed") }}</a>
            <a v-else :href="activity.course_link" target="_blank">{{
              t("activities.course_link")
            }}</a>
          </div>
        </div>
      </div>
      <div v-else class="not-found">
        {{ t("activities.not_found") }}
      </div>

      <div class="activities-back-link">
        <router-link to="/activities">↼ {{ t("activities.back_to_list") }}</router-link>
      </div>
    </div>
  </div>
  <FooterSection />
</template>

<script setup>
import { useLayoutStore } from "@/stores/layout";
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import FooterSection from "@/components/FooterSection.vue";
import { activitiesAPI } from "@/services/api.js";
import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const layoutStore = useLayoutStore();
const route = useRoute();
const { t, locale } = useI18n();

const activity = ref({});
const loading = ref(true);

const {
  showLightbox,
  currentLightboxImage,
  openLightbox,
  closeLightbox,
  handleBodyClick,
} = useLightbox();

const fetchDetail = async () => {
  loading.value = true;
  try {
    const id = route.params.id;
    if (!id) {
      activity.value = {};
      return;
    }

    const res = await activitiesAPI.get(id);
    activity.value = res?.data ?? {};
  } catch (e) {
    activity.value = {};
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchDetail();
});

// Helper functions
const getTitle = (item) => {
  if (!item) return "";
  if (locale.value === "en-US") {
    return item.title_en || item.title;
  }
  return item.title;
};

const parseImageCaptions = (html, defaultAlt = "") => {
  if (!html) return "";
  return html.replace(/<img([^>]+)>/gi, (imgTag, attrs) => {
    const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
    let altText = altMatch ? altMatch[1].trim() : "";
    let finalAttrs = attrs;
    const fallbackAlt = defaultAlt ? `${defaultAlt} 內容圖片` : "活動內容圖片";

    if (!altMatch || !altText) {
      if (!altMatch) {
        finalAttrs = ` alt="${fallbackAlt}"${attrs}`;
      } else {
        finalAttrs = attrs.replace(/alt=["'][^"']*["']/i, `alt="${fallbackAlt}"`);
      }
      altText = fallbackAlt;
    }

    if (altText && altText !== fallbackAlt) {
      return `<figure class="rich-figure"><img${finalAttrs}><figcaption class="image-caption">${altText}</figcaption></figure>`;
    }
    return `<img${finalAttrs}>`;
  });
};

const getContent = (item) => {
  if (!item) return "";
  let val = "";
  if (locale.value === "en-US") {
    val = item.content_en || item.content;
  } else {
    val = item.content;
  }
  return parseImageCaptions(val, getTitle(item));
};

const getImage = (item) => {
  if (!item) return "";
  if (locale.value === "en-US") {
    return item.featured_image_en || item.featured_image;
  }
  return item.featured_image;
};

const getLocation = (item) => {
  if (!item) return "";
  if (locale.value === "en-US") {
    return item.locate_en || item.locate;
  }
  return item.locate;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatTime = (startTime) => {
  if (!startTime) return "";
  let result = startTime.slice(0, 5);
  return result;
};

// 取得按鈕狀態
const getButtonStatus = (item) => {
  const now = new Date();

  // 1. 尚未開放報名
  if (!item.is_registration_open) return "not_open";

  // 2. 活動已結束 (或是活動已開始)
  // 根據需求表：活動開始日期「已過去」 -> 活動已結束
  if (item.start_date) {
    const startTimeStr = item.start_time || "00:00:00";
    const startDateTimeStr = `${item.start_date}T${startTimeStr}`;
    const startDateTime = new Date(startDateTimeStr);

    if (now > startDateTime) {
      return "ended";
    }
  }

  // 3. 報名已截止
  // 根據需求表：活動開始日期「尚未到」且 報名截止時間「已過去」
  if (item.registration_end_time) {
    // 這裡只須判斷截止時間，因為如果活動已開始，上面已經 return "ended" 了
    const regEndDateTime = new Date(item.registration_end_time);
    if (now > regEndDateTime) {
      return "registration_closed";
    }
  }

  // 4. 前往報名
  return "register";
};

const isActivityEnded = (item) => {
  if (!item.end_date) return false;

  const endTimeStr = item.end_time || "23:59:59";
  const endDateTimeStr = `${item.end_date}T${endTimeStr}`;
  const endDateTime = new Date(endDateTimeStr);
  const now = new Date();

  return now > endDateTime;
};
</script>

<style scoped lang="scss">
@use "@/styles/rich-content.scss" as rc;

.all-courses-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.activities-area {
  background: linear-gradient(0deg,
      rgba(226, 242, 255, 0.8),
      rgba(226, 242, 255, 0.8));
  background-size: cover;
  width: 100%;
  // margin-top: 120px;
  // enabled announcement-banner
  margin-top: v-bind("layoutStore.pageMarginTop");

  @media (max-width: 768px) {
    margin-top: v-bind('layoutStore.showAnnouncementBanner ? "160px" : "55px"');
  }
}

.activities-container {
  max-width: 1200px;
  margin: 0 auto;
  height: 200px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    padding: 40px 20px;
  }
}

.activities-content-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
}

.activities-date {
  font-size: 20px;
  font-weight: 400;
  color: #666666;
  margin: 0 0 10px 0;
}

.activities-title {
  font-size: 32px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 20px;
  box-sizing: border-box;
  flex: 1;
}

.feature-image-container {
  width: 100%;
  margin-bottom: 30px;
  background-color: rgba(255, 255, 255, 0.5);
  /* Semi-transparent white base */
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
  position: relative;
  display: flex;
  justify-content: center;
}

.feature-image {
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

:deep(.activities-body) {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
  margin-left: auto;
  margin-right: auto;



  /* 套用統一 rich-content mixin，包含 H2/H3 分層樣式、對齊、figcaption */
  @include rc.rich-content;

  /* 覆寫 mixin 中的段落字級 */
  p,
  li {
    font-family: "Noto Sans TC", sans-serif;
    font-size: 20px;
    line-height: 1.8;
  }

  /* 圖片：全寬 + 放大鏡游標 */
  img {
    width: 100%;
    height: auto;
    display: block;
    margin: 20px auto;
    cursor: zoom-in;
  }

  /* 斜體 */
  i {
    font-style: oblique;
    font-synthesis: style;
  }
}

.info-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.info-item {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.links-section {
  margin-bottom: 40px;
}

.reg-link,
.course-link {
  font-size: 16px;
  margin-bottom: 10px;

  a {
    color: #1171dd;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  .disabled-link {
    color: #999;
    cursor: not-allowed;
    text-decoration: none;
    pointer-events: none;
  }
}

.activities-back-link {
  margin-top: 20px;

  a {
    color: #1171dd;
    border-bottom: 1px solid #1171dd;
    text-decoration: none;
    transition: all 0.3s;

    &:hover {
      border-bottom-color: transparent;
    }
  }
}

.loading-container {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
}
</style>
